import { ChevronLeftIcon, ChevronRightIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { format, isSameMonth } from 'date-fns';
import { useState } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { isPickableMonth } from '../../monthPeriod';

const MONTHS = Array.from({ length: 12 }, (_, index) => index);

/**
 * A month and year picker: the year with ‹ › and its twelve months, those after today's month off. A pick hands the
 * month's first day to `onPick`; the year arrows only browse.
 */
export function MonthPicker({ month, onPick }: { month: Date; onPick: (month: Date) => void }) {
    const today = new Date();
    const [year, setYear] = useState(month.getFullYear());
    // A Clear or another change of the period brings the browsed year back to it; a remount would drop focus.
    const [shownMonth, setShownMonth] = useState(month.getTime());
    if (shownMonth !== month.getTime()) {
        setShownMonth(month.getTime());
        setYear(month.getFullYear());
    }
    return (
        <div className="flex w-56 flex-col gap-2">
            <div className="flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('time.previous-year')}
                    onClick={() => setYear(year - 1)}
                >
                    <ChevronLeftIcon />
                </Button>
                <span className="text-meta font-semibold tabular-nums">{year}</span>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('time.next-year')}
                    disabled={year >= today.getFullYear()}
                    onClick={() => setYear(year + 1)}
                >
                    <ChevronRightIcon />
                </Button>
            </div>
            <div role="group" aria-label={String(year)} className="grid grid-cols-3 gap-1">
                {MONTHS.map((index) => {
                    const candidate = new Date(year, index, 1);
                    const isPicked = isSameMonth(candidate, month);
                    return (
                        <Button
                            key={index}
                            variant={isPicked ? 'default' : 'ghost'}
                            size="sm"
                            aria-pressed={isPicked}
                            aria-label={format(candidate, 'MMMM yyyy')}
                            disabled={!isPickableMonth(candidate, today)}
                            onClick={() => onPick(candidate)}
                            className={cn('text-meta', !isPicked && 'font-normal')}
                        >
                            {format(candidate, 'MMM')}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
}
